import { alpha, createTheme, keyframes } from '@mui/material/styles';

export const animations = {
  glowPulse: keyframes`
    0%, 100% {
      box-shadow: 0 0 20px rgba(0, 255, 255, 0.3),
                  0 0 40px rgba(0, 255, 255, 0.15),
                  inset 0 0 20px rgba(0, 255, 255, 0.05);
    }
    50% {
      box-shadow: 0 0 30px rgba(0, 255, 255, 0.5),
                  0 0 60px rgba(0, 255, 255, 0.25),
                  inset 0 0 30px rgba(0, 255, 255, 0.1);
    }
  `,
  borderGlow: keyframes`
    0%, 100% { border-color: rgba(0, 255, 255, 0.5); }
    50% { border-color: rgba(255, 0, 255, 0.5); }
  `,
  scanLine: keyframes`
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  `,
  dataStream: keyframes`
    0% { background-position: 0% 0%; }
    100% { background-position: 0% 100%; }
  `,
  float: keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    25% { transform: translateY(-5px) rotate(0.5deg); }
    75% { transform: translateY(5px) rotate(-0.5deg); }
  `,
  shimmer: keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  `,
  textGlow: keyframes`
    0%, 100% {
      text-shadow: 0 0 10px rgba(0, 255, 255, 0.8),
                   0 0 20px rgba(0, 255, 255, 0.5),
                   0 0 30px rgba(0, 255, 255, 0.3);
    }
    50% {
      text-shadow: 0 0 15px rgba(0, 255, 255, 1),
                   0 0 30px rgba(0, 255, 255, 0.7),
                   0 0 45px rgba(0, 255, 255, 0.4);
    }
  `,
  pulseRing: keyframes`
    0% { transform: scale(0.95); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.7; }
    100% { transform: scale(0.95); opacity: 1; }
  `,
  rotateGradient: keyframes`
    0% { filter: hue-rotate(0deg); }
    100% { filter: hue-rotate(360deg); }
  `,
  chipFloat: keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  `,
  fadeSlideIn: keyframes`
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
  `,
  fadeSlideUp: keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  `,
  neonFlicker: keyframes`
    0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% {
      text-shadow:
        0 0 4px #fff,
        0 0 11px #fff,
        0 0 19px #fff,
        0 0 40px #00ffff,
        0 0 80px #00ffff,
        0 0 90px #00ffff,
        0 0 100px #00ffff,
        0 0 150px #00ffff;
    }
    20%, 24%, 55% {
      text-shadow: none;
    }
  `,
  hologramGlitch: keyframes`
    0% { transform: translate(0); }
    20% { transform: translate(-2px, 2px); }
    40% { transform: translate(-2px, -2px); }
    60% { transform: translate(2px, 2px); }
    80% { transform: translate(2px, -2px); }
    100% { transform: translate(0); }
  `,
  energyPulse: keyframes`
    0% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.05); }
    100% { opacity: 0.6; transform: scale(1); }
  `,
};

export const neonColors = {
  cyan: '#00ffff',
  magenta: '#ff00ff',
  lime: '#00ff88',
  amber: '#ffff00',
  orange: '#ff6600',
  crimson: '#ff4444',
  violet: '#8b5cf6',
  darkBase: '#0a0a0f',
  darkMid: '#1a1a2e',
  darkAlt: '#0f0f1a',
  glassLight: 'rgba(255, 255, 255, 0.05)',
  glassMedium: 'rgba(255, 255, 255, 0.1)',
  glassHeavy: 'rgba(255, 255, 255, 0.15)',
  textPrimary: 'rgba(255, 255, 255, 0.95)',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  textDisabled: 'rgba(255, 255, 255, 0.3)',
};

export const gradients = {
  darkCanvas: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0f0f1a 100%)',
  cyanMagenta: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',
  cyanViolet: 'linear-gradient(135deg, #00ffff 0%, #8b5cf6 100%)',
  magentaCyan: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',
  neonRainbow: 'linear-gradient(135deg, #00ffff 0%, #00ff88 25%, #ff00ff 75%, #00ffff 100%)',
  warmNeon: 'linear-gradient(135deg, #ff6600 0%, #ff00ff 100%)',
  coolNeon: 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)',
  glassCard: (color = neonColors.cyan) =>
    `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, rgba(0, 0, 0, 0.7) 100%)`,
  shimmer: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
  scanLines: `repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(0, 255, 255, 0.03) 2px,
    rgba(0, 255, 255, 0.03) 4px
  )`,
  dataPanel: 'linear-gradient(180deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 255, 255, 0.02) 100%)',
  heroHeader: 'linear-gradient(135deg, rgba(0, 255, 255, 0.08) 0%, rgba(255, 0, 255, 0.05) 50%, rgba(0, 0, 0, 0.9) 100%)',
};

export const neonShadows = {
  cyanGlow: (intensity = 0.3) => `0 0 20px ${alpha(neonColors.cyan, intensity)}, 0 0 40px ${alpha(neonColors.cyan, intensity * 0.5)}`,
  magentaGlow: (intensity = 0.3) => `0 0 20px ${alpha(neonColors.magenta, intensity)}, 0 0 40px ${alpha(neonColors.magenta, intensity * 0.5)}`,
  cardHover: (color = neonColors.cyan) => `0 20px 60px ${alpha(color, 0.3)}, 0 0 30px ${alpha(color, 0.2)}`,
  cardElevated: (color = neonColors.cyan) => `0 8px 32px ${alpha(color, 0.2)}, 0 0 20px ${alpha(color, 0.1)}`,
  buttonHover: (color = neonColors.cyan) => `0 8px 20px ${alpha(color, 0.4)}`,
  textNeon: (color = neonColors.cyan) => `0 0 10px ${alpha(color, 0.8)}, 0 0 20px ${alpha(color, 0.5)}, 0 0 30px ${alpha(color, 0.3)}`,
  insetGlow: (color = neonColors.cyan) => `inset 0 0 20px ${alpha(color, 0.1)}`,
};

export const getCyberTheme = (options = {}) => {
  const {
    primaryColor = neonColors.cyan,
    secondaryColor = neonColors.magenta,
    accentColor = neonColors.lime,
    scaledFontSize = 14,
  } = options;
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: primaryColor,
        light: alpha(primaryColor, 0.8),
        dark: alpha(primaryColor, 0.6),
        contrastText: '#000000',
      },
      secondary: {
        main: secondaryColor,
        light: alpha(secondaryColor, 0.8),
        dark: alpha(secondaryColor, 0.6),
        contrastText: '#ffffff',
      },
      background: {
        default: neonColors.darkBase,
        paper: neonColors.darkMid,
      },
      text: {
        primary: neonColors.textPrimary,
        secondary: neonColors.textSecondary,
        disabled: neonColors.textDisabled,
      },
      divider: alpha(primaryColor, 0.2),
      error: {
        main: neonColors.crimson,
      },
      warning: {
        main: neonColors.orange,
      },
      success: {
        main: neonColors.lime,
      },
      info: {
        main: primaryColor,
      },
      neon: {
        cyan: neonColors.cyan,
        magenta: neonColors.magenta,
        lime: neonColors.lime,
        amber: neonColors.amber,
        orange: neonColors.orange,
        crimson: neonColors.crimson,
        violet: neonColors.violet,
      },
      glass: {
        light: neonColors.glassLight,
        medium: neonColors.glassMedium,
        heavy: neonColors.glassHeavy,
      },
      sidebarSection: {
        workoutSessions: {
          main: primaryColor,
          bg: alpha(primaryColor, 0.12)
        },
        myCalendars: {
          main: neonColors.violet,
          bg: alpha(neonColors.violet, 0.12)
        },
        workoutPlans: {
          main: accentColor,
          bg: alpha(accentColor, 0.12)
        },
        workoutLogs: {
          main: neonColors.amber,
          bg: alpha(neonColors.amber, 0.12)
        },
        locations: {
          main: neonColors.cyan,
          bg: alpha(neonColors.cyan, 0.12)
        },
        activities: {
          main: neonColors.magenta,
          bg: alpha(neonColors.magenta, 0.12)
        },
        equipment: {
          main: '#64748b',
          bg: alpha('#64748b', 0.12)
        },
        statistics: {
          main: '#64748b',
          bg: alpha('#64748b', 0.12)
        },
      },
    },
    typography: {
      fontFamily: '"Rajdhani", "Orbitron", "Roboto Mono", sans-serif',
      fontSize: scaledFontSize,
      h1: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 900,
        letterSpacing: '4px',
        textTransform: 'uppercase',
        lineHeight: 1.2,
      },
      h2: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 800,
        letterSpacing: '3px',
        textTransform: 'uppercase',
        lineHeight: 1.3,
      },
      h3: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 700,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        lineHeight: 1.3,
      },
      h4: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 700,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        lineHeight: 1.4,
      },
      h5: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 600,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        lineHeight: 1.4,
      },
      h6: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 600,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        lineHeight: 1.5,
      },
      body1: {
        fontFamily: '"Rajdhani", sans-serif',
        fontWeight: 500,
        letterSpacing: '0.5px',
        lineHeight: 1.7,
      },
      body2: {
        fontFamily: '"Rajdhani", sans-serif',
        fontWeight: 500,
        letterSpacing: '0.5px',
        lineHeight: 1.6,
      },
      button: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 600,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        fontSize: '0.85rem',
      },
      caption: {
        fontFamily: '"Rajdhani", sans-serif',
        fontWeight: 500,
        letterSpacing: '0.5px',
        fontSize: '0.75rem',
      },
      overline: {
        fontFamily: '"Orbitron", sans-serif',
        fontWeight: 600,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        fontSize: '0.7rem',
      },
      subtitle1: {
        fontFamily: '"Rajdhani", sans-serif',
        fontWeight: 600,
        letterSpacing: '0.5px',
      },
      subtitle2: {
        fontFamily: '"Rajdhani", sans-serif',
        fontWeight: 600,
        letterSpacing: '0.5px',
      },
    },
    shape: {
      borderRadius: 16,
    },
    shadows: [
      'none',
      `0 2px 8px ${alpha(primaryColor, 0.1)}`,
      `0 4px 12px ${alpha(primaryColor, 0.12)}`,
      `0 6px 16px ${alpha(primaryColor, 0.14)}`,
      `0 8px 20px ${alpha(primaryColor, 0.16)}`,
      `0 10px 24px ${alpha(primaryColor, 0.18)}`,
      `0 12px 28px ${alpha(primaryColor, 0.2)}`,
      `0 14px 32px ${alpha(primaryColor, 0.22)}`,
      `0 16px 36px ${alpha(primaryColor, 0.24)}`,
      `0 18px 40px ${alpha(primaryColor, 0.26)}`,
      `0 20px 44px ${alpha(primaryColor, 0.28)}`,
      `0 22px 48px ${alpha(primaryColor, 0.3)}`,
      `0 24px 52px ${alpha(primaryColor, 0.32)}`,
      `0 26px 56px ${alpha(primaryColor, 0.34)}`,
      `0 28px 60px ${alpha(primaryColor, 0.36)}`,
      `0 30px 64px ${alpha(primaryColor, 0.38)}`,
      `0 32px 68px ${alpha(primaryColor, 0.4)}`,
      `0 34px 72px ${alpha(primaryColor, 0.42)}`,
      `0 36px 76px ${alpha(primaryColor, 0.44)}`,
      `0 38px 80px ${alpha(primaryColor, 0.46)}`,
      `0 40px 84px ${alpha(primaryColor, 0.48)}`,
      `0 42px 88px ${alpha(primaryColor, 0.5)}`,
      `0 44px 92px ${alpha(primaryColor, 0.52)}`,
      `0 46px 96px ${alpha(primaryColor, 0.54)}`,
      `0 48px 100px ${alpha(primaryColor, 0.56)}`,
    ],
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
        bounce: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        elastic: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        smooth: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
        glow: 400,
        hover: 300,
        pulse: 2000,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          // @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Rajdhani:wght@400;500;600;700&family=Roboto+Mono:wght@400;500&display=swap');
          * {
            box-sizing: border-box;
          }
          html {
            scroll-behavior: smooth;
          }
          body {
            background: ${gradients.darkCanvas};
            min-height: 100vh;
            overflow-x: hidden;
          }
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.3);
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, ${primaryColor}, ${secondaryColor});
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, ${alpha(primaryColor, 0.8)}, ${alpha(secondaryColor, 0.8)});
          }
          ::selection {
            background: ${alpha(primaryColor, 0.3)};
            color: ${neonColors.textPrimary};
          }
          *:focus-visible {
            outline: 2px solid ${primaryColor};
            outline-offset: 2px;
          }
        `,
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'transparent',
          },
          elevation0: {
            background: 'transparent',
          },
          elevation1: {
            background: gradients.glassCard(primaryColor),
            backdropFilter: 'blur(16px)',
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
            borderRadius: 16,
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)`,
              opacity: 0.5,
              transition: 'opacity 0.3s ease',
            },
          },
          elevation2: {
            background: gradients.heroHeader,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
            borderRadius: 24,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '200%',
              height: '100%',
              background: gradients.shimmer,
              animation: `${animations.shimmer} 3s infinite`,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              background: gradients.cyanMagenta,
              borderRadius: 26,
              zIndex: -1,
              opacity: 0.5,
              filter: 'blur(10px)',
              animation: `${animations.rotateGradient} 5s linear infinite`,
            },
          },
          elevation3: {
            background: gradients.dataPanel,
            border: `1px solid ${alpha(primaryColor, 0.2)}`,
            borderRadius: 20,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)`,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${secondaryColor}, transparent)`,
            },
          },
          outlined: {
            background: 'transparent',
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
            backdropFilter: 'blur(8px)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: gradients.glassCard(primaryColor),
            backdropFilter: 'blur(16px)',
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
            borderRadius: 16,
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            overflow: 'hidden',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 24,
            '&:last-child': {
              paddingBottom: 24,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 30,
            padding: '10px 28px',
            fontSize: '0.85rem',
            fontWeight: 600,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
              transition: 'left 0.5s ease',
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              '&::before': {
                left: '100%',
              },
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          contained: {
            background: gradients.cyanViolet,
            color: '#000000',
            fontWeight: 700,
            boxShadow: 'none',
            '&:hover': {
              background: gradients.cyanMagenta,
              boxShadow: neonShadows.buttonHover(primaryColor),
            },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.8)} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(primaryColor, 0.9)} 0%, ${primaryColor} 100%)`,
              boxShadow: neonShadows.buttonHover(primaryColor),
            },
          },
          containedSecondary: {
            background: `linear-gradient(135deg, ${secondaryColor} 0%, ${alpha(secondaryColor, 0.8)} 100%)`,
            color: '#ffffff',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(secondaryColor, 0.9)} 0%, ${secondaryColor} 100%)`,
              boxShadow: neonShadows.buttonHover(secondaryColor),
            },
          },
          outlined: {
            borderWidth: 2,
            borderColor: primaryColor,
            color: primaryColor,
            background: alpha(primaryColor, 0.05),
            backdropFilter: 'blur(8px)',
            '&:hover': {
              borderWidth: 2,
              borderColor: primaryColor,
              background: alpha(primaryColor, 0.15),
              boxShadow: `0 0 20px ${alpha(primaryColor, 0.3)}`,
            },
          },
          outlinedPrimary: {
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: primaryColor,
              background: alpha(primaryColor, 0.15),
            },
          },
          outlinedSecondary: {
            borderColor: secondaryColor,
            color: secondaryColor,
            '&:hover': {
              borderColor: secondaryColor,
              background: alpha(secondaryColor, 0.15),
            },
          },
          text: {
            color: primaryColor,
            '&:hover': {
              background: alpha(primaryColor, 0.1),
              textShadow: neonShadows.textNeon(primaryColor),
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.3s ease',
            '&:hover': {
              background: alpha(primaryColor, 0.15),
              '& svg': {
                filter: `drop-shadow(0 0 8px ${primaryColor})`,
              },
            },
          },
          colorPrimary: {
            color: primaryColor,
            '&:hover': {
              background: alpha(primaryColor, 0.15),
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            background: gradients.cyanMagenta,
            boxShadow: neonShadows.cyanGlow(0.4),
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'rotate(5deg)',
              boxShadow: neonShadows.cyanGlow(0.6),
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            borderRadius: 8,
            transition: 'all 0.3s ease',
          },
          filled: {
            background: `linear-gradient(135deg, ${alpha(primaryColor, 0.2)} 0%, ${alpha(primaryColor, 0.05)} 100%)`,
            border: `1px solid ${alpha(primaryColor, 0.5)}`,
            color: primaryColor,
            backdropFilter: 'blur(8px)',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(primaryColor, 0.4)} 0%, ${alpha(primaryColor, 0.15)} 100%)`,
              boxShadow: `0 0 20px ${alpha(primaryColor, 0.5)}, 0 0 40px ${alpha(primaryColor, 0.2)}`,
            },
          },
          outlined: {
            borderColor: alpha(primaryColor, 0.5),
            color: primaryColor,
            background: alpha(primaryColor, 0.05),
            '&:hover': {
              background: alpha(primaryColor, 0.15),
              borderColor: primaryColor,
            },
          },
          colorPrimary: {
            background: `linear-gradient(135deg, ${alpha(primaryColor, 0.2)} 0%, ${alpha(primaryColor, 0.05)} 100%)`,
            color: primaryColor,
          },
          colorSecondary: {
            background: `linear-gradient(135deg, ${alpha(secondaryColor, 0.2)} 0%, ${alpha(secondaryColor, 0.05)} 100%)`,
            color: secondaryColor,
            border: `1px solid ${alpha(secondaryColor, 0.5)}`,
          },
          icon: {
            color: 'inherit',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            background: `linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, ${alpha(primaryColor, 0.05)} 100%)`,
            borderRadius: 16,
            padding: 8,
            border: `1px solid ${alpha(primaryColor, 0.2)}`,
            backdropFilter: 'blur(10px)',
            minHeight: 56,
          },
          indicator: {
            height: '100%',
            borderRadius: 12,
            background: `linear-gradient(135deg, ${alpha(primaryColor, 0.3)} 0%, ${alpha(secondaryColor, 0.2)} 100%)`,
            boxShadow: `0 0 20px ${alpha(primaryColor, 0.5)}`,
            zIndex: 0,
          },
          flexContainer: {
            gap: 4,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 600,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            color: neonColors.textMuted,
            zIndex: 1,
            minHeight: 48,
            borderRadius: 12,
            transition: 'all 0.3s ease',
            '&.Mui-selected': {
              color: primaryColor,
              textShadow: `0 0 10px ${alpha(primaryColor, 0.8)}`,
            },
            '&:hover': {
              color: primaryColor,
            },
            '&.Mui-disabled': {
              color: neonColors.textDisabled,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: alpha('#ffffff', 0.03),
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: alpha(primaryColor, 0.3),
              borderWidth: 1,
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
              borderColor: alpha(primaryColor, 0.5),
            },
            '&.Mui-focused': {
              backgroundColor: alpha(primaryColor, 0.05),
              '& fieldset': {
                borderColor: primaryColor,
                borderWidth: 2,
                boxShadow: `0 0 15px ${alpha(primaryColor, 0.3)}`,
              },
            },
            '&.Mui-error fieldset': {
              borderColor: neonColors.crimson,
            },
          },
          input: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 500,
            letterSpacing: '0.5px',
            '&::placeholder': {
              color: neonColors.textMuted,
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            letterSpacing: '0.5px',
            color: neonColors.textSecondary,
            '&.Mui-focused': {
              color: primaryColor,
            },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: neonColors.textMuted,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: primaryColor,
            transition: 'transform 0.3s ease',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            // FIX START: Ensure solid background for readability
            backgroundColor: '#0a0a0f', 
            backgroundImage: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)',
            // FIX END
            backdropFilter: 'none',
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
            borderRadius: 12,
            marginTop: 8,
          },
          list: {
            padding: 8,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: '#0a0a0f',
            backgroundImage: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)',
            backdropFilter: 'none',
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
            borderRadius: 12,
            marginTop: 8,
            boxShadow: `0 4px 20px ${alpha(primaryColor, 0.2)}`,
          },
          option: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 500,
            borderRadius: 8,
            margin: '2px 8px',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: alpha(primaryColor, 0.15),
            },
            '&[aria-selected="true"]': {
              background: alpha(primaryColor, 0.2),
              color: primaryColor,
              '&:hover': {
                background: alpha(primaryColor, 0.25),
              },
            },
            '&.Mui-focused': {
              background: alpha(primaryColor, 0.15),
            }
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 500,
            borderRadius: 8,
            margin: '2px 0',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: alpha(primaryColor, 0.15),
            },
            '&.Mui-selected': {
              background: alpha(primaryColor, 0.2),
              color: primaryColor,
              '&:hover': {
                background: alpha(primaryColor, 0.25),
              },
            },
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: 8,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            margin: '4px 8px',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: alpha(primaryColor, 0.1),
            },
            '&.Mui-selected': {
              background: alpha(primaryColor, 0.15),
              color: primaryColor,
              '& .MuiListItemIcon-root': {
                color: primaryColor,
              },
              '&:hover': {
                background: alpha(primaryColor, 0.25),
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: '60%',
                background: primaryColor,
                borderRadius: '0 4px 4px 0',
                boxShadow: `0 0 10px ${primaryColor}`,
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: neonColors.textSecondary,
            minWidth: 40,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            letterSpacing: '0.5px',
          },
          secondary: {
            fontFamily: '"Rajdhani", sans-serif',
            color: neonColors.textMuted,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: `linear-gradient(135deg, ${alpha('#0a0a0f', 0.98)} 0%, ${alpha('#1a1a2e', 0.98)} 100%)`,
            backdropFilter: 'blur(30px)',
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
            borderRadius: 24,
            boxShadow: `0 0 60px ${alpha(primaryColor, 0.2)}, 0 0 120px ${alpha(secondaryColor, 0.1)}`,
          },
          paperFullScreen: {
            borderRadius: 0,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 700,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            fontSize: '1.25rem',
            padding: '24px 32px 16px',
            color: primaryColor,
            textShadow: neonShadows.textNeon(primaryColor),
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '16px 32px 24px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 32px 24px',
            gap: 12,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: alpha('#000000', 0.8),
            backdropFilter: 'blur(8px)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            letterSpacing: '0.5px',
            fontSize: '0.8rem',
            background: `linear-gradient(135deg, ${alpha('#0a0a0f', 0.95)} 0%, ${alpha('#1a1a2e', 0.95)} 100%)`,
            border: `1px solid ${alpha(primaryColor, 0.4)}`,
            borderRadius: 8,
            padding: '8px 16px',
            boxShadow: `0 0 20px ${alpha(primaryColor, 0.3)}`,
          },
          arrow: {
            color: alpha(primaryColor, 0.4),
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backdropFilter: 'blur(16px)',
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 500,
          },
          standardError: {
            background: alpha(neonColors.crimson, 0.15),
            border: `1px solid ${alpha(neonColors.crimson, 0.5)}`,
            color: neonColors.crimson,
          },
          standardWarning: {
            background: alpha(neonColors.orange, 0.15),
            border: `1px solid ${alpha(neonColors.orange, 0.5)}`,
            color: neonColors.orange,
          },
          standardInfo: {
            background: alpha(primaryColor, 0.15),
            border: `1px solid ${alpha(primaryColor, 0.5)}`,
            color: primaryColor,
          },
          standardSuccess: {
            background: alpha(neonColors.lime, 0.15),
            border: `1px solid ${alpha(neonColors.lime, 0.5)}`,
            color: neonColors.lime,
          },
          icon: {
            filter: 'drop-shadow(0 0 8px currentColor)',
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiPaper-root': {
              background: `linear-gradient(135deg, ${alpha('#0a0a0f', 0.95)} 0%, ${alpha('#1a1a2e', 0.95)} 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(primaryColor, 0.3)}`,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: primaryColor,
            filter: `drop-shadow(0 0 10px ${primaryColor})`,
          },
          circle: {
            strokeLinecap: 'round',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 6,
            borderRadius: 3,
            backgroundColor: alpha(primaryColor, 0.15),
          },
          bar: {
            borderRadius: 3,
            background: gradients.cyanMagenta,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: primaryColor,
            height: 6,
          },
          track: {
            background: gradients.cyanMagenta,
            border: 'none',
          },
          rail: {
            backgroundColor: alpha(primaryColor, 0.2),
          },
          thumb: {
            width: 20,
            height: 20,
            background: gradients.cyanMagenta,
            border: `2px solid ${primaryColor}`,
            boxShadow: `0 0 15px ${primaryColor}`,
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 25px ${primaryColor}`,
            },
            '&.Mui-active': {
              boxShadow: `0 0 30px ${primaryColor}`,
            },
          },
          valueLabel: {
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 600,
            background: alpha('#0a0a0f', 0.9),
            border: `1px solid ${primaryColor}`,
            borderRadius: 8,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 52,
            height: 28,
            padding: 0,
          },
          switchBase: {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(24px)',
              '& + .MuiSwitch-track': {
                backgroundColor: primaryColor,
                opacity: 1,
                boxShadow: `0 0 15px ${primaryColor}`,
              },
              '& .MuiSwitch-thumb': {
                background: '#ffffff',
                boxShadow: `0 0 10px ${primaryColor}`,
              },
            },
          },
          thumb: {
            width: 24,
            height: 24,
            background: neonColors.textMuted,
            boxShadow: 'none',
          },
          track: {
            borderRadius: 14,
            backgroundColor: alpha('#ffffff', 0.15),
            opacity: 1,
            border: `1px solid ${alpha(primaryColor, 0.3)}`,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: alpha(primaryColor, 0.5),
            '&.Mui-checked': {
              color: primaryColor,
              filter: `drop-shadow(0 0 8px ${primaryColor})`,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: alpha(primaryColor, 0.5),
            '&.Mui-checked': {
              color: primaryColor,
              filter: `drop-shadow(0 0 8px ${primaryColor})`,
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            background: gradients.cyanMagenta,
            border: `2px solid ${alpha(primaryColor, 0.5)}`,
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 700,
          },
        },
      },
      MuiAvatarGroup: {
        styleOverrides: {
          avatar: {
            borderColor: neonColors.darkBase,
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 700,
            fontSize: '0.65rem',
          },
          colorPrimary: {
            background: gradients.cyanMagenta,
            boxShadow: `0 0 10px ${primaryColor}`,
          },
          colorSecondary: {
            background: secondaryColor,
            boxShadow: `0 0 10px ${secondaryColor}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: alpha(primaryColor, 0.2),
            '&::before, &::after': {
              borderColor: alpha(primaryColor, 0.2),
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            background: gradients.glassCard(primaryColor),
            backdropFilter: 'blur(16px)',
            border: `1px solid ${alpha(primaryColor, 0.2)}`,
            borderRadius: '16px !important',
            marginBottom: 8,
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: '0 0 8px 0',
              border: `1px solid ${alpha(primaryColor, 0.4)}`,
              boxShadow: `0 0 20px ${alpha(primaryColor, 0.2)}`,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 600,
            letterSpacing: '1px',
            minHeight: 56,
            '&.Mui-expanded': {
              minHeight: 56,
            },
          },
          expandIconWrapper: {
            color: primaryColor,
            transition: 'transform 0.3s ease',
            '&.Mui-expanded': {
              transform: 'rotate(180deg)',
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '0 24px 24px',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            background: gradients.glassCard(primaryColor),
            backdropFilter: 'blur(16px)',
            border: `1px solid ${alpha(primaryColor, 0.2)}`,
            borderRadius: 16,
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: 'separate',
            borderSpacing: '0 4px',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontFamily: '"Orbitron", sans-serif',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              color: primaryColor,
              borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`,
              background: alpha(primaryColor, 0.05),
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root': {
              transition: 'all 0.2s ease',
              '&:hover': {
                background: alpha(primaryColor, 0.1),
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 500,
            borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
            padding: '16px',
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            fontFamily: '"Rajdhani", sans-serif',
          },
          selectLabel: {
            fontFamily: '"Rajdhani", sans-serif',
          },
          displayedRows: {
            fontFamily: '"Rajdhani", sans-serif',
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 500,
          },
          separator: {
            color: primaryColor,
          },
          li: {
            '& a': {
              color: neonColors.textSecondary,
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: primaryColor,
                textShadow: `0 0 10px ${primaryColor}`,
              },
            },
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            background: 'transparent',
          },
        },
      },
      MuiStep: {
        styleOverrides: {
          root: {
            '& .MuiStepLabel-iconContainer': {
              '& .MuiStepIcon-root': {
                color: alpha(primaryColor, 0.3),
                '&.Mui-active': {
                  color: primaryColor,
                  filter: `drop-shadow(0 0 10px ${primaryColor})`,
                },
                '&.Mui-completed': {
                  color: neonColors.lime,
                  filter: `drop-shadow(0 0 10px ${neonColors.lime})`,
                },
              },
            },
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            '&.Mui-active': {
              color: primaryColor,
            },
            '&.Mui-completed': {
              color: neonColors.lime,
            },
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: {
            borderColor: alpha(primaryColor, 0.3),
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(primaryColor, 0.1),
            '&::after': {
              background: `linear-gradient(90deg, transparent, ${alpha(primaryColor, 0.1)}, transparent)`,
            },
          },
        },
      },
      MuiRating: {
        styleOverrides: {
          iconFilled: {
            color: neonColors.amber,
            filter: `drop-shadow(0 0 6px ${neonColors.amber})`,
          },
          iconEmpty: {
            color: alpha(neonColors.amber, 0.3),
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: `linear-gradient(180deg, ${alpha('#0a0a0f', 0.98)} 0%, ${alpha('#1a1a2e', 0.98)} 100%)`,
            backdropFilter: 'blur(30px)',
            borderRight: `1px solid ${alpha(primaryColor, 0.2)}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: `linear-gradient(90deg, ${alpha('#0a0a0f', 0.95)} 0%, ${alpha('#1a1a2e', 0.95)} 100%)`,
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${alpha(primaryColor, 0.2)}`,
            boxShadow: `0 4px 30px ${alpha('#000000', 0.5)}`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '64px !important',
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            background: `linear-gradient(0deg, ${alpha('#0a0a0f', 0.98)} 0%, ${alpha('#1a1a2e', 0.95)} 100%)`,
            backdropFilter: 'blur(20px)',
            borderTop: `1px solid ${alpha(primaryColor, 0.2)}`,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: neonColors.textMuted,
            transition: 'all 0.3s ease',
            '&.Mui-selected': {
              color: primaryColor,
              '& svg': {
                filter: `drop-shadow(0 0 8px ${primaryColor})`,
              },
            },
          },
          label: {
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 600,
            fontSize: '0.65rem',
            letterSpacing: '0.5px',
          },
        },
      },
      MuiSpeedDial: {
        styleOverrides: {
          fab: {
            background: gradients.cyanMagenta,
            boxShadow: neonShadows.cyanGlow(0.4),
            '&:hover': {
              background: gradients.magentaCyan,
            },
          },
        },
      },
      MuiSpeedDialAction: {
        styleOverrides: {
          fab: {
            background: alpha('#0a0a0f', 0.9),
            border: `1px solid ${alpha(primaryColor, 0.4)}`,
            color: primaryColor,
            '&:hover': {
              background: alpha(primaryColor, 0.2),
            },
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              fontFamily: '"Orbitron", sans-serif',
              fontWeight: 600,
              color: neonColors.textSecondary,
              border: `1px solid ${alpha(primaryColor, 0.2)}`,
              background: alpha('#0a0a0f', 0.5),
              transition: 'all 0.3s ease',
              '&:hover': {
                background: alpha(primaryColor, 0.2),
                borderColor: primaryColor,
              },
              '&.Mui-selected': {
                background: alpha(primaryColor, 0.3),
                borderColor: primaryColor,
                color: primaryColor,
                boxShadow: `0 0 15px ${alpha(primaryColor, 0.4)}`,
              },
            },
          },
        },
      },
      MuiTimelineItem: {
        styleOverrides: {
          root: {
            '&::before': {
              flex: 0,
              padding: 0,
            },
          },
        },
      },
      MuiTimelineDot: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
          filled: {
            background: gradients.cyanMagenta,
            boxShadow: `0 0 15px ${primaryColor}`,
          },
          outlined: {
            borderColor: primaryColor,
            boxShadow: `0 0 10px ${alpha(primaryColor, 0.4)}`,
          },
        },
      },
      MuiTimelineConnector: {
        styleOverrides: {
          root: {
            background: `linear-gradient(180deg, ${primaryColor}, ${secondaryColor})`,
            width: 2,
          },
        },
      },
    },
  });
};

export const styledComponentUtils = {
  futuristicContainer: {
    position: 'relative',
    background: gradients.darkCanvas,
    minHeight: '100vh',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: gradients.scanLines,
      pointerEvents: 'none',
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '4px',
      background: `linear-gradient(90deg, transparent, ${neonColors.cyan}, transparent)`,
      animation: `${animations.scanLine} 4s linear infinite`,
      pointerEvents: 'none',
      zIndex: 2,
    },
  },
  glassCard: (color = neonColors.cyan) => ({
    position: 'relative',
    background: gradients.glassCard(color),
    backdropFilter: 'blur(16px)',
    border: `1px solid ${alpha(color, 0.3)}`,
    borderRadius: 16,
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
      opacity: 0.5,
      transition: 'opacity 0.3s ease',
    },
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: neonShadows.cardHover(color),
      border: `1px solid ${alpha(color, 0.6)}`,
      '&::before': {
        opacity: 1,
      },
    },
  }),
  neonText: (color = neonColors.cyan) => ({
    color: color,
    textShadow: neonShadows.textNeon(color),
    letterSpacing: '1px',
  }),
  cyberTitle: {
    fontFamily: '"Orbitron", sans-serif',
    fontWeight: 900,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    background: gradients.neonRainbow,
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: `${animations.shimmer} 4s linear infinite`,
  },
  hexagonIcon: (color = neonColors.cyan) => ({
    width: 60,
    height: 60,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${alpha(color, 0.3)} 0%, ${alpha(color, 0.1)} 100%)`,
    clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
    animation: `${animations.pulseRing} 3s ease-in-out infinite`,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 2,
      background: 'rgba(0, 0, 0, 0.8)',
      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
    },
    '& svg': {
      position: 'relative',
      zIndex: 1,
      color: color,
      filter: `drop-shadow(0 0 10px ${color})`,
    },
  }),
  stepNumber: (color = neonColors.cyan) => ({
    width: 48,
    height: 48,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${alpha(color, 0.3)} 0%, ${alpha(neonColors.magenta, 0.2)} 100%)`,
    border: `2px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Orbitron", sans-serif',
    fontWeight: 900,
    fontSize: '1.2rem',
    color: color,
    textShadow: `0 0 10px ${alpha(color, 0.8)}`,
    boxShadow: `0 0 20px ${alpha(color, 0.3)}, inset 0 0 10px ${alpha(color, 0.1)}`,
    flexShrink: 0,
    animation: `${animations.pulseRing} 2s ease-in-out infinite`,
  }),
  dataPanel: {
    position: 'relative',
    background: gradients.dataPanel,
    border: `1px solid ${alpha(neonColors.cyan, 0.2)}`,
    borderRadius: 20,
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: `linear-gradient(90deg, transparent, ${neonColors.cyan}, transparent)`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: `linear-gradient(90deg, transparent, ${neonColors.magenta}, transparent)`,
    },
  },
  videoCard: {
    position: 'relative',
    background: 'rgba(0, 0, 0, 0.8)',
    border: `1px solid ${alpha(neonColors.magenta, 0.3)}`,
    borderRadius: 16,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    '&:hover': {
      transform: 'translateY(-10px) rotateX(5deg)',
      boxShadow: `0 30px 60px ${alpha(neonColors.magenta, 0.3)}, 0 0 40px ${alpha(neonColors.magenta, 0.2)}`,
      border: `1px solid ${alpha(neonColors.magenta, 0.8)}`,
    },
  },
  loadingOrb: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${alpha(neonColors.cyan, 0.2)} 0%, ${alpha(neonColors.magenta, 0.2)} 100%)`,
    border: '2px solid transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: `${animations.pulseRing} 2s ease-in-out infinite`,
    boxShadow: `0 0 40px ${alpha(neonColors.cyan, 0.3)}, 0 0 80px ${alpha(neonColors.magenta, 0.2)}`,
  },
  backgroundOrb: (color, size = 300, blur = 60) => ({
    position: 'absolute',
    width: size,
    height: size,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${alpha(color, 0.1)} 0%, transparent 70%)`,
    filter: `blur(${blur}px)`,
    pointerEvents: 'none',
  }),
};

export default getCyberTheme;